import { useEffect, useMemo, useState } from "react";
import { convexErrMessage } from "@/lib/convex-error";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useAuthState } from "@/data/user";
import { useAuth } from "@/hooks/use-auth";
import { buildEvalsPath, navigateApp } from "@/lib/app-navigation";
import { Button } from "@sphinx/design-system/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@sphinx/design-system/dialog";
import { useHostList, type HostListItem } from "@/hooks/useClients";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@sphinx/design-system/tooltip";
import { WIZARD_STEPS, STORAGE_KEYS, DEFAULTS } from "./constants";
import { ServersStep } from "./eval-runner/ServersStep";
import { TargetsStep } from "./eval-runner/TargetsStep";
import { TestsStep } from "./eval-runner/TestsStep";
import { ReviewStep } from "./eval-runner/ReviewStep";
import type {
  AvailableTool,
  TestTemplate,
  ExpectedToolCall,
} from "./eval-runner/types";
import { useSharedAppState } from "@/state/app-state-context";
import { sphinxApi } from "@/lib/sphinx-api";
import {
  generateEvalTests,
  generateNegativeEvalTests,
  listEvalTools,
  type GeneratedEvalTestCase,
} from "@/lib/apis/evals-api";
import { resolvePromptTurns, type PromptTurn } from "@/shared/steps";
import { promptTurnsToSteps } from "@/shared/steps";
import { DEFAULT_YARN_ORG, type TargetId, type YarnEnv, type YarnOrg } from "@/shared/sphinx";
import {
  createSphinxRun,
  useCreateTestCaseMutation,
  useCreateTestSuiteMutation,
} from "@/data/evals";
import type { EvalCase } from "./types";

/** An existing suite to run: the wizard skips server/test authoring and only
 *  picks targets + yarn env, then creates a sphinx run for its cases — all of
 *  them, or just `caseIds` (single-case quick run with no enabled target). */
export interface RunWizardSuite {
  id: string;
  name: string;
  cases: EvalCase[];
  caseIds?: string[];
}

/** The cases a run wizard will submit: `caseIds` when set, else every case. */
export function runWizardCases(suite: RunWizardSuite): EvalCase[] {
  if (!suite.caseIds) return suite.cases;
  const wanted = new Set(suite.caseIds);
  return suite.cases.filter((c) => wanted.has(c._id));
}

interface EvalRunnerProps {
  projectId: string;
  inline?: boolean;
  onSuccess?: (suiteId?: string) => void;
  preselectedServer?: string;
  suite?: RunWizardSuite;
  /** Controlled dialog state (used with `suite`); uncontrolled renders its own trigger. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

type StepKey = (typeof WIZARD_STEPS)[number]["key"];

export const DEFAULT_YARN_ENV: YarnEnv = "dev";

const SUITE_RUN_STEP_KEYS: readonly StepKey[] = ["targets", "review"];

/** Suite cases → the template shape the review step renders. */
export function casesToTemplates(cases: EvalCase[]): TestTemplate[] {
  return cases.map((c) => ({
    title: c.title || "Untitled case",
    query: c.query ?? "",
    runs: 1,
    expectedToolCalls: (c.expectedToolCalls ?? []).map((tc) => ({
      toolName: tc.toolName,
      arguments: (tc.arguments ?? {}) as Record<string, unknown>,
    })),
    isNegativeTest: c.isNegativeTest,
    expectedOutput: c.expectedOutput,
    yarnOrgs: (c.yarnOrgs ?? []) as TestTemplate["yarnOrgs"],
  }));
}

const buildBlankTestTemplate = (): TestTemplate => ({
  title: ``,
  query: "",
  runs: DEFAULTS.RUNS_PER_TEST,
  expectedToolCalls: [],
});

const buildBlankNegativeTestTemplate = (): TestTemplate => ({
  title: ``,
  query: "",
  runs: DEFAULTS.RUNS_PER_TEST,
  expectedToolCalls: [],
  isNegativeTest: true,
  scenario: "",
});

const validateExpectedToolCalls = (toolCalls: ExpectedToolCall[]): boolean => {
  // Must have at least one tool call
  if (toolCalls.length === 0) {
    return false;
  }

  // Check each tool call
  for (const toolCall of toolCalls) {
    // Tool name must not be empty
    if (!toolCall.toolName || toolCall.toolName.trim() === "") {
      return false;
    }

    // Check all argument values are not empty strings
    if (toolCall.arguments) {
      for (const value of Object.values(toolCall.arguments)) {
        // Only fail on empty strings, not other falsy values
        if (value === "") {
          return false;
        }
      }
    }
  }

  return true;
};

function getFirstPromptTurn(
  promptTurns: PromptTurn[] | undefined,
): PromptTurn | undefined {
  return Array.isArray(promptTurns) && promptTurns.length > 0
    ? promptTurns[0]
    : undefined;
}

function promptTurnsRepresentNegativeCase(
  promptTurns: PromptTurn[] | undefined,
): boolean {
  return (
    Array.isArray(promptTurns) &&
    promptTurns.length > 0 &&
    promptTurns.every((turn) => turn.expectedToolCalls.length === 0)
  );
}

function mapGeneratedTemplate(
  test: GeneratedEvalTestCase,
  index: number,
): TestTemplate {
  const promptTurns =
    Array.isArray(test.promptTurns) && test.promptTurns.length > 0
      ? test.promptTurns
      : undefined;
  const firstTurn = getFirstPromptTurn(promptTurns);

  return {
    title: test.title || `Generated test ${index + 1}`,
    query: test.query || firstTurn?.prompt || "",
    runs: Number(test.runs) > 0 ? Number(test.runs) : 1,
    expectedToolCalls: firstTurn?.expectedToolCalls
      ? firstTurn.expectedToolCalls
      : Array.isArray(test.expectedToolCalls)
        ? test.expectedToolCalls
        : [],
    isNegativeTest:
      test.isNegativeTest === true ||
      promptTurnsRepresentNegativeCase(promptTurns),
    scenario: test.scenario || "",
    expectedOutput: test.expectedOutput || firstTurn?.expectedOutput,
    promptTurns,
  };
}

export function EvalRunner({
  projectId,
  inline = false,
  onSuccess,
  preselectedServer,
  suite,
  open: controlledOpen,
  onOpenChange,
}: EvalRunnerProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  // Existing suite: targets + review only. Servers come from each target's
  // config (Targets page) and the cases are the suite's.
  const steps = useMemo(
    () =>
      suite
        ? WIZARD_STEPS.filter((s) => SUITE_RUN_STEP_KEYS.includes(s.key))
        : WIZARD_STEPS,
    [suite],
  );
  const stepIndex = (key: StepKey) => steps.findIndex((s) => s.key === key);
  const lastStep = steps.length - 1;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingNegativeTests, setIsGeneratingNegativeTests] =
    useState(false);
  // Start at step 1 (targets) if server is preselected, otherwise step 0 (servers)
  const hasPreselectedServer =
    preselectedServer && preselectedServer !== "none";
  const initialStep = suite ? 0 : hasPreselectedServer ? 1 : 0;
  const [currentStep, setCurrentStep] = useState(initialStep);
  // Test-case runs never target prod (David, 2026-08-25) — no env picker, dev only.
  const yarnEnv: YarnEnv = DEFAULT_YARN_ENV;
  const [yarnOrg, setYarnOrg] = useState<YarnOrg>(DEFAULT_YARN_ORG);
  const [savedPreferences, setSavedPreferences] = useState<{
    servers: string[];
    targetIds: string[];
  } | null>(null);
  const { isAuthenticated } = useAuthState();
  const { getAccessToken } = useAuth();
  const appState = useSharedAppState();
  const createTestSuite = useCreateTestSuiteMutation();
  const createTestCase = useCreateTestCaseMutation();
  // The fixed 10-client matrix; `enabled` seeds this run's selection. What
  // each client RUNS (its models) and attaches (its connectors) is resolved
  // server-side from the target row, so only ids go over the wire.
  const { hosts: targets, isLoading: targetsLoading } = useHostList({
    isAuthenticated,
    projectId,
  });

  // Initialize with preselected server if provided
  const [selectedServers, setSelectedServers] = useState<string[]>(() => {
    if (hasPreselectedServer) {
      return [preselectedServer];
    }
    return [];
  });
  const [selectedTargetIds, setSelectedTargetIds] = useState<string[]>([]);
  const selectedTargets = useMemo<HostListItem[]>(
    () => targets.filter((t) => selectedTargetIds.includes(t.hostId)),
    [targets, selectedTargetIds],
  );
  const [testTemplates, setTestTemplates] = useState<TestTemplate[]>([
    buildBlankTestTemplate(),
  ]);
  // Auto-name suite after server if preselected
  const [suiteName, setSuiteName] = useState(
    hasPreselectedServer ? preselectedServer : "",
  );
  const [suiteDescription, setSuiteDescription] = useState("");
  const [showNameError, setShowNameError] = useState(false);
  const [hasRestoredPreferences, setHasRestoredPreferences] = useState(false);
  const [availableTools, setAvailableTools] = useState<AvailableTool[]>([]);

  // Pass/fail criteria state
  const [minimumPassRate, setMinimumPassRate] = useState<number>(
    DEFAULTS.MIN_PASS_RATE,
  );

  const connectedServers = useMemo(
    () =>
      Object.entries(appState.servers).filter(
        ([, server]) => server.connectionStatus === "connected",
      ),
    [appState.servers],
  );

  const connectedServerNames = useMemo(
    () => new Set(connectedServers.map(([name]) => name)),
    [connectedServers],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.EVAL_RUNNER_PREFERENCES);
      if (!stored) return;
      const parsed = JSON.parse(stored) as {
        servers?: string[];
        targetIds?: string[];
        passCriteria?: {
          minimumPassRate?: number;
        };
      };
      setSavedPreferences({
        servers: parsed.servers ?? [],
        targetIds: parsed.targetIds ?? [],
      });

      // Restore pass criteria preferences
      if (parsed.passCriteria) {
        if (parsed.passCriteria.minimumPassRate !== undefined) {
          setMinimumPassRate(parsed.passCriteria.minimumPassRate);
        }
      }
    } catch (error) {
      console.warn("Failed to load eval runner preferences", error);
    }
  }, []);

  useEffect(() => {
    if (!savedPreferences) return;
    // Don't restore server preferences if we have a preselected server
    if (hasPreselectedServer) return;

    if (savedPreferences.servers?.length) {
      const filtered = savedPreferences.servers.filter((server) =>
        connectedServerNames.has(server),
      );
      if (filtered.length) {
        setSelectedServers(filtered);
      }
    }
  }, [savedPreferences, connectedServerNames, hasPreselectedServer]);

  useEffect(() => {
    // Only restore preferences once on initial load. Seed from the targets
    // enabled on the Targets page; a previously saved per-run selection
    // (still valid) wins over that.
    if (hasRestoredPreferences) return;
    if (targetsLoading || targets.length === 0) return;

    const knownIds = new Set(targets.map((t) => t.hostId));
    const saved = (savedPreferences?.targetIds ?? []).filter((id) =>
      knownIds.has(id),
    );
    setSelectedTargetIds(
      saved.length > 0
        ? saved
        : targets.filter((t) => t.enabled).map((t) => t.hostId),
    );
    setHasRestoredPreferences(true);
  }, [targets, targetsLoading, savedPreferences, hasRestoredPreferences]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const payload = {
        servers: selectedServers,
        targetIds: selectedTargetIds,
        passCriteria: {
          minimumPassRate,
        },
      };
      localStorage.setItem(
        STORAGE_KEYS.EVAL_RUNNER_PREFERENCES,
        JSON.stringify(payload),
      );
    } catch (error) {
      console.warn("Failed to persist eval runner preferences", error);
    }
  }, [selectedServers, selectedTargetIds, minimumPassRate]);

  // Fetch available tools from the selected servers — or, before any are
  // selected, from every connector, so the expected-tools editor offers a
  // dropdown instead of a bare text input.
  useEffect(() => {
    async function fetchTools() {
      let serverIds = selectedServers;
      if (serverIds.length === 0) {
        try {
          serverIds = (await sphinxApi.servers.list()).map((row) => row.name);
        } catch {
          serverIds = [];
        }
      }
      if (serverIds.length === 0) {
        setAvailableTools([]);
        return;
      }

      try {
        const data = await listEvalTools({
          projectId,
          serverIds,
        });
        setAvailableTools(data.tools || []);
      } catch (error) {
        console.error("Failed to fetch tools:", error);
      }
    }

    fetchTools();
  }, [selectedServers, projectId]);

  useEffect(() => {
    if (!inline && !open) {
      setCurrentStep(initialStep);
      setHasRestoredPreferences(false);
    }
  }, [inline, open, initialStep]);

  const validTestTemplates = useMemo(
    () =>
      suite
        ? casesToTemplates(runWizardCases(suite))
        : testTemplates.filter((template) => template.query.trim().length > 0),
    [suite, testTemplates],
  );

  const stepCompletion = useMemo(() => {
    // Check if all valid test templates are properly configured
    // Positive tests need valid expected tool calls
    // Negative tests need scenario and query
    const allTestsAreValid = validTestTemplates.every((template) => {
      if (template.isNegativeTest) {
        // Negative tests need scenario and query
        return (
          template.query.trim().length > 0 &&
          (template.scenario?.trim().length ?? 0) > 0
        );
      }
      // Positive tests need valid expected tool calls
      return validateExpectedToolCalls(template.expectedToolCalls);
    });

    return {
      // Suite runs resolve servers per target on the server side.
      servers: suite ? true : selectedServers.length > 0,
      // A client with no model of its own is fine — the server falls back to
      // its `models` list and then to the provider default.
      targets: selectedTargets.length > 0,
      // Existing cases were validated when saved; only require at least one.
      tests: suite
        ? suite.cases.length > 0
        : validTestTemplates.length > 0 && allTestsAreValid,
    };
  }, [suite, selectedServers, selectedTargets, validTestTemplates]);

  const stepComplete = (key: StepKey) =>
    key === "review"
      ? stepCompletion.servers && stepCompletion.targets && stepCompletion.tests
      : stepCompletion[key];

  const highestAvailableStep = useMemo(() => {
    const firstIncomplete = steps.findIndex(
      (s) => s.key !== "review" && !stepComplete(s.key),
    );
    return firstIncomplete === -1 ? lastStep : firstIncomplete;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, stepCompletion, lastStep]);

  const currentStepKey = steps[currentStep]?.key ?? steps[0].key;
  const canAdvance = stepComplete(currentStepKey);

  const toggleServer = (name: string) => {
    setSelectedServers((prev) => {
      if (prev.includes(name)) {
        return prev.filter((server) => server !== name);
      }
      return [...prev, name];
    });
  };

  const handleToggleTarget = (targetId: string) => {
    setSelectedTargetIds((prev) =>
      prev.includes(targetId)
        ? prev.filter((id) => id !== targetId)
        : [...prev, targetId],
    );
  };

  const handleAddTestTemplate = () => {
    setTestTemplates((prev) => [...prev, buildBlankTestTemplate()]);
  };

  const handleAddNegativeTestTemplate = () => {
    setTestTemplates((prev) => [...prev, buildBlankNegativeTestTemplate()]);
  };

  const handleRemoveTestTemplate = (index: number) => {
    setTestTemplates((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateTestTemplate = <K extends keyof TestTemplate>(
    index: number,
    field: K,
    value: TestTemplate[K],
  ) => {
    setTestTemplates((prev) => {
      const next = [...prev];
      const currentTemplate = next[index];
      if (!currentTemplate) {
        return prev;
      }

      const nextTemplate: TestTemplate = {
        ...currentTemplate,
        [field]: value,
      };

      const firstTurn = getFirstPromptTurn(nextTemplate.promptTurns);
      if (firstTurn) {
        if (field === "query") {
          nextTemplate.promptTurns = [
            {
              ...firstTurn,
              prompt: typeof value === "string" ? value : firstTurn.prompt,
            },
            ...nextTemplate.promptTurns!.slice(1),
          ];
        } else if (field === "expectedToolCalls") {
          nextTemplate.promptTurns = [
            {
              ...firstTurn,
              expectedToolCalls: Array.isArray(value)
                ? (value as ExpectedToolCall[])
                : firstTurn.expectedToolCalls,
            },
            ...nextTemplate.promptTurns!.slice(1),
          ];
        }
      }

      next[index] = nextTemplate;
      return next;
    });
  };

  const handleGenerateTests = async () => {
    track("eval_generate_tests_button_clicked", {
      location: "eval_runner",
      step: currentStep,
    });

    if (!isAuthenticated) {
      toast.error("Please sign in to generate tests");
      return;
    }

    if (selectedServers.length === 0) {
      toast.error("Please select at least one server");
      return;
    }

    setIsGenerating(true);

    try {
      const accessToken = await getAccessToken();
      const result = await generateEvalTests({
        projectId,
        serverIds: selectedServers,
        convexAuthToken: accessToken,
      });

      if (result.tests && result.tests.length > 0) {
        const generatedTemplates = result.tests.map(mapGeneratedTemplate);

        setTestTemplates(generatedTemplates);
        setCurrentStep(2);
        toast.success(
          `Generated ${generatedTemplates.length} test template(s).`,
        );
      }
    } catch (error) {
      console.error("Failed to generate tests:", error);
      toast.error(
        convexErrMessage(error, "Failed to generate test cases"),
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateNegativeTests = async () => {
    track("eval_generate_negative_tests_button_clicked", {
      location: "eval_runner",
      step: currentStep,
    });

    if (!isAuthenticated) {
      toast.error("Please sign in to generate negative tests");
      return;
    }

    if (selectedServers.length === 0) {
      toast.error("Please select at least one server");
      return;
    }

    setIsGeneratingNegativeTests(true);

    try {
      const accessToken = await getAccessToken();
      const result = await generateNegativeEvalTests({
        projectId,
        serverIds: selectedServers,
        convexAuthToken: accessToken,
      });

      if (result.tests && result.tests.length > 0) {
        const generatedNegativeTemplates = result.tests.map((test, index) => ({
          ...mapGeneratedTemplate(test, index),
          expectedToolCalls: [],
          isNegativeTest: true,
        }));

        // Append to existing templates instead of replacing
        setTestTemplates((prev) => [...prev, ...generatedNegativeTemplates]);
        setCurrentStep(2);
        toast.success(
          `Generated ${generatedNegativeTemplates.length} negative test template(s).`,
        );
      }
    } catch (error) {
      console.error("Failed to generate negative tests:", error);
      toast.error(
        convexErrMessage(error, "Failed to generate negative test cases"),
      );
    } finally {
      setIsGeneratingNegativeTests(false);
    }
  };

  const handleNext = () => {
    if (currentStep >= lastStep) return;
    if (!canAdvance) return;
    setCurrentStep((prev) => Math.min(prev + 1, lastStep));
  };

  const handleBack = () => {
    if (currentStep === 0) return;
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  /** Resolve the suite to run (create it + its cases first when authoring
   *  from scratch), then POST /eval-suites/:id/runs {targets, yarnEnv,
   *  yarnOrg, caseIds?}. */
  const startRun = async (): Promise<{ suiteId: string; runId: string }> => {
    let suiteId = suite?.id;
    if (!suiteId) {
      const created = await createTestSuite({
        name: suiteName.trim(),
        description: suiteDescription.trim() || undefined,
        defaultPassCriteria: { minimumPassRate },
      });
      suiteId = created._id;
      for (const template of validTestTemplates) {
        await createTestCase({
          suiteId,
          title: template.title || template.query.slice(0, 60),
          query: template.query,
          models: [],
          runs: template.runs,
          expectedToolCalls: template.expectedToolCalls,
          isNegativeTest: template.isNegativeTest,
          scenario: template.scenario,
          expectedOutput: template.expectedOutput,
          // Pinned orgs ride in the case json; the run expands per org.
          yarnOrgs: template.yarnOrgs?.length ? template.yarnOrgs : undefined,
          steps: promptTurnsToSteps(
            resolvePromptTurns({
              promptTurns: template.promptTurns,
              query: template.query,
              expectedToolCalls: template.expectedToolCalls,
              expectedOutput: template.expectedOutput,
            }),
          ),
        });
      }
    }
    const run = await createSphinxRun(suiteId, {
      targets: selectedTargetIds as TargetId[],
      yarnEnv,
      yarnOrg,
      ...(suite?.caseIds ? { caseIds: suite.caseIds } : {}),
    });
    return { suiteId, runId: run.id };
  };

  const handleSubmit = async () => {
    if (!suite && selectedServers.length === 0) {
      toast.error("Please select at least one server");
      setCurrentStep(stepIndex("servers"));
      return;
    }

    if (selectedTargets.length === 0) {
      toast.error("Please select at least one target");
      setCurrentStep(stepIndex("targets"));
      return;
    }
    // No model check here. Which models a client runs is the server's call:
    // its `models` list, else its single `model`, else the provider default
    // (`modelsForTarget`, server/queue/enqueue.ts). Refusing a client whose
    // legacy `model` column happened to be null used to block the very clients
    // the multi-model picker configures.

    if (validTestTemplates.length === 0) {
      toast.error(
        suite
          ? "This suite has no test cases yet"
          : "Please add at least one test template with a query",
      );
      if (!suite) setCurrentStep(stepIndex("tests"));
      return;
    }

    if (!suite && !suiteName.trim()) {
      setShowNameError(true);
      return;
    }
    setShowNameError(false);

    setIsSubmitting(true);
    try {
      const { suiteId, runId } = await startRun();
      toast.success("Run started");
      track("eval_suite_run_started", {
        location: "eval_runner",
        suite_id: suiteId,
        num_test_cases: validTestTemplates.length,
        num_targets: selectedTargets.length,
        yarn_env: yarnEnv,
        yarn_org: yarnOrg,
      });

      if (!inline) setOpen(false);
      else onSuccess?.(suiteId);
      navigateApp(buildEvalsPath({ type: "run-detail", suiteId, runId }));

      setTestTemplates([buildBlankTestTemplate()]);
      setSuiteName("");
      setSuiteDescription("");
      setShowNameError(false);
      setCurrentStep(initialStep);
    } catch (error) {
      toast.error(convexErrMessage(error, "Failed to start run"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStepKey) {
      case "servers":
        return (
          <ServersStep
            connectedServers={connectedServers}
            selectedServers={selectedServers}
            onToggleServer={toggleServer}
          />
        );
      case "targets":
        return (
          <TargetsStep
            targets={targets}
            selectedTargetIds={selectedTargetIds}
            onToggleTarget={handleToggleTarget}
          />
        );
      case "tests":
        return (
          <TestsStep
            testTemplates={testTemplates}
            availableTools={availableTools}
            canGenerateTests={stepCompletion.servers && stepCompletion.targets}
            hasServerAndModelSelection={
              stepCompletion.servers && stepCompletion.targets
            }
            isGenerating={isGenerating}
            isGeneratingNegativeTests={isGeneratingNegativeTests}
            onGenerateTests={handleGenerateTests}
            onGenerateNegativeTests={handleGenerateNegativeTests}
            onAddTestTemplate={handleAddTestTemplate}
            onAddNegativeTestTemplate={handleAddNegativeTestTemplate}
            onRemoveTestTemplate={handleRemoveTestTemplate}
            onUpdateTestTemplate={handleUpdateTestTemplate}
          />
        );
      case "review":
        return (
          <ReviewStep
            suiteName={suite ? suite.name : suiteName}
            suiteDescription={suiteDescription}
            minimumPassRate={minimumPassRate}
            selectedServers={selectedServers}
            selectedTargets={selectedTargets}
            validTestTemplates={validTestTemplates}
            yarnOrg={yarnOrg}
            onYarnOrgChange={setYarnOrg}
            existingSuite={Boolean(suite)}
            onSuiteNameChange={setSuiteName}
            onSuiteDescriptionChange={setSuiteDescription}
            onMinimumPassRateChange={setMinimumPassRate}
            onEditStep={(key) => {
              const idx = stepIndex(key);
              if (idx >= 0) setCurrentStep(idx);
            }}
            showNameError={showNameError}
          />
        );
      default:
        return null;
    }
  };

  const stepper = (
    <ol className="flex flex-col items-center gap-4 text-center md:flex-row md:gap-6">
      {steps.map((step, index) => {
        const isActive = currentStep === index;
        const isCompleted =
          index < currentStep && index <= highestAvailableStep;
        const isSelectable =
          index <= Math.max(highestAvailableStep, currentStep);
        return (
          <li key={step.key} className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => (isSelectable ? setCurrentStep(index) : undefined)}
              disabled={!isSelectable}
              className={cn(
                "flex flex-col items-center gap-2 transition",
                !isSelectable && "cursor-not-allowed opacity-60",
              )}
            >
              <span
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full border text-sm font-medium",
                  isCompleted &&
                    "border-primary bg-primary text-primary-foreground",
                  isActive &&
                    !isCompleted &&
                    "border-primary bg-primary/10 text-primary",
                  !isActive &&
                    !isCompleted &&
                    "border-border text-muted-foreground",
                )}
              >
                {index + 1}
              </span>
              <span
                className={cn(
                  "text-xs leading-tight",
                  isActive ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {step.title}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );

  const nextDisabled =
    currentStep < lastStep
      ? !canAdvance
      : isSubmitting || !canAdvance;

  const nextVariant = nextDisabled ? "secondary" : "default";

  // Determine tooltip message for Next button
  const getNextButtonTooltip = (): string => {
    if (!nextDisabled) {
      return currentStep === lastStep
        ? "Start the eval run"
        : "Continue to next step";
    }

    switch (currentStepKey) {
      case "servers":
        return "Select at least one server to continue";
      case "targets":
        return "Select at least one target; every selected target needs a model configured";
      case "tests": {
        const hasInvalidPositiveTests = validTestTemplates.some(
          (template) =>
            !template.isNegativeTest &&
            !validateExpectedToolCalls(template.expectedToolCalls),
        );
        const hasInvalidNegativeTests = validTestTemplates.some(
          (template) =>
            template.isNegativeTest &&
            (!template.scenario?.trim() || !template.query.trim()),
        );
        if (hasInvalidPositiveTests) {
          return "All tool names must be specified and argument values cannot be empty";
        }
        if (hasInvalidNegativeTests) {
          return "Negative tests require both a scenario description and user prompt";
        }
        return "Add at least one test case with a query to continue";
      }
      case "review":
        return suite && suite.cases.length === 0
          ? "This suite has no test cases yet"
          : "Complete all required fields to start the eval run";
      default:
        return "Complete the current step to continue";
    }
  };

  const handleClose = () => {
    if (inline) {
      onSuccess?.();
    } else {
      setOpen(false);
    }
  };

  const wizardLayout = (
    <div
      className={cn(
        "mx-auto flex w-full flex-col gap-8 pb-10 pt-4",
        inline ? "max-w-none px-4 sm:px-6 md:px-12 lg:px-32" : "max-w-3xl px-4",
      )}
    >
      <div className="flex flex-wrap items-center gap-4">
        {inline && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label="Close"
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 0}
          aria-label="Back"
          className="justify-center gap-2"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>
        <div className="flex flex-1 justify-center">
          <div className="max-w-xl">{stepper}</div>
        </div>
        {nextDisabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  type="button"
                  variant={nextVariant}
                  onClick={() => {
                    if (currentStep < lastStep) {
                      track("eval_setup_next_step_button_clicked", {
                        location: "eval_runner",
                        step: currentStep,
                      });
                      handleNext();
                    } else {
                      track("eval_setup_start_eval_run_button_clicked", {
                        location: "eval_runner",
                        step: currentStep,
                      });
                      void handleSubmit();
                    }
                  }}
                  disabled={nextDisabled}
                  aria-label={
                    currentStep === lastStep ? "Start" : "Next"
                  }
                  className={cn(
                    "justify-center gap-2",
                    !nextDisabled && "shadow-sm",
                  )}
                >
                  {currentStep === lastStep ? "Start" : "Next"}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{getNextButtonTooltip()}</TooltipContent>
          </Tooltip>
        ) : (
          <Button
            type="button"
            variant={nextVariant}
            onClick={() => {
              if (currentStep < lastStep) {
                track("eval_setup_next_step_button_clicked", {
                  location: "eval_runner",
                  step: currentStep,
                });
                handleNext();
              } else {
                track("eval_setup_start_eval_run_button_clicked", {
                  location: "eval_runner",
                  step: currentStep,
                });
                void handleSubmit();
              }
            }}
            disabled={nextDisabled}
            aria-label={
              currentStep === lastStep ? "Start" : "Next"
            }
            className={cn("justify-center gap-2", !nextDisabled && "shadow-sm")}
          >
            {currentStep === lastStep ? "Start" : "Next"}
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="space-y-6">{renderStepContent()}</div>
    </div>
  );

  if (inline) {
    return wizardLayout;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {controlledOpen === undefined ? (
        <DialogTrigger asChild>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            New eval run
          </Button>
        </DialogTrigger>
      ) : null}
      <DialogContent
        className="max-h-[80vh] w-full max-w-4xl overflow-y-auto sm:max-w-4xl"
        data-testid="eval-run-wizard"
      >
        <DialogHeader className="mx-auto w-full max-w-3xl gap-1 text-left">
          <DialogTitle>{suite ? `Run ${suite.name}` : "Create eval run"}</DialogTitle>
          <DialogDescription>
            {suite?.caseIds
              ? "No client is enabled on the Clients page. Pick the clients and Yarn environment for this case, then start."
              : suite
                ? "Pick the targets and Yarn environment, then start a run across every case in this suite."
                : "Follow the guided steps to configure your evaluation and run it with confidence."}
          </DialogDescription>
        </DialogHeader>
        {wizardLayout}
      </DialogContent>
    </Dialog>
  );
}
